import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  UploadedFile,
  Language,
  FlowStep,
  DocumentSummaryDto,
  BatchRiskAnalysisResponseDto,
  TranslationJobResponseDto,
} from '@/types';

export interface TranslationFlowState {
  /** Current step in the 3-step workflow */
  step: FlowStep;
  /** Upload batch ID returned by POST /documents */
  uploadBatchId: string | null;
  /** Source language code (e.g. 'en') */
  sourceLang: string | null;
  /** Target language codes (e.g. ['fr', 'de']) */
  targetLangs: string[];
  /** Source language object for UI display */
  sourceLanguage: Language | null;
  /** Target language objects for UI display */
  targetLanguages: Language[];
  /** Files added locally by the user (before upload) */
  localFiles: UploadedFile[];
  /** Document summaries returned by the backend */
  files: DocumentSummaryDto[];
  /** Risk analysis response from the backend */
  riskAnalysis: BatchRiskAnalysisResponseDto | null;
  /** User-applied route overrides: documentId -> decision */
  overrides: Record<string, string>;
  /** Job IDs created via lane decisions */
  jobIds: string[];
  /** Full translation job details from the backend */
  jobs: TranslationJobResponseDto[];
}

const initialState: TranslationFlowState = {
  step: 'upload',
  uploadBatchId: null,
  sourceLang: null,
  targetLangs: [],
  sourceLanguage: null,
  targetLanguages: [],
  localFiles: [],
  files: [],
  riskAnalysis: null,
  overrides: {},
  jobIds: [],
  jobs: [],
};

const translationFlowSlice = createSlice({
  name: 'translationFlow',
  initialState,
  reducers: {
    setStep(state, action: PayloadAction<FlowStep>) {
      state.step = action.payload;
    },
    setUploadBatch(state, action: PayloadAction<string>) {
      state.uploadBatchId = action.payload;
    },
    setLanguages(state, action: PayloadAction<{ sourceLang: string; targetLangs: string[] }>) {
      state.sourceLang = action.payload.sourceLang;
      state.targetLangs = action.payload.targetLangs;
    },
    setSourceLanguage(state, action: PayloadAction<Language>) {
      state.sourceLanguage = action.payload;
      state.sourceLang = action.payload.code;
    },
    setTargetLanguages(state, action: PayloadAction<Language[]>) {
      state.targetLanguages = action.payload;
      state.targetLangs = action.payload.map((l) => l.code);
    },
    setLocalFiles(state, action: PayloadAction<UploadedFile[]>) {
      state.localFiles = action.payload;
    },
    addLocalFiles(state, action: PayloadAction<UploadedFile[]>) {
      state.localFiles.push(...action.payload);
    },
    removeLocalFile(state, action: PayloadAction<string>) {
      state.localFiles = state.localFiles.filter((f) => f.id !== action.payload);
    },
    setFiles(state, action: PayloadAction<DocumentSummaryDto[]>) {
      state.files = action.payload;
    },
    setRiskAnalysis(state, action: PayloadAction<BatchRiskAnalysisResponseDto>) {
      state.riskAnalysis = action.payload;
    },
    setOverride(state, action: PayloadAction<{ documentId: string; decision: string }>) {
      state.overrides[action.payload.documentId] = action.payload.decision;
    },
    clearOverrides(state) {
      state.overrides = {};
    },
    addJobId(state, action: PayloadAction<string>) {
      if (!state.jobIds.includes(action.payload)) {
        state.jobIds.push(action.payload);
      }
    },
    updateJobs(state, action: PayloadAction<TranslationJobResponseDto[]>) {
      state.jobs = action.payload;
    },
    /**
     * Add or replace a single job by jobId. Used after a retry to inject the
     * freshly-created (non-terminal) job, which un-settles the batch and re-arms
     * polling; the next poll then reconciles the slice to server truth.
     */
    upsertJob(state, action: PayloadAction<TranslationJobResponseDto>) {
      const idx = state.jobs.findIndex((j) => j.jobId === action.payload.jobId);
      if (idx >= 0) state.jobs[idx] = action.payload;
      else state.jobs.push(action.payload);
      if (!state.jobIds.includes(action.payload.jobId)) state.jobIds.push(action.payload.jobId);
    },
    /**
     * Add or replace each job by jobId, keeping the jobs the payload lacks. Used for a one-off refresh of the
     * batch (COPA-737): replacing the whole list as updateJobs does could drop a retry job that upsertJob added
     * while the refresh was in flight, and leave the batch settled with nothing polling it. Jobs of any batch
     * other than the current one are ignored, so a refresh that lands after resetFlow cannot seed a new flow.
     */
    mergeJobs(state, action: PayloadAction<TranslationJobResponseDto[]>) {
      for (const job of action.payload) {
        if (job.uploadBatchId !== state.uploadBatchId) continue;
        const idx = state.jobs.findIndex((j) => j.jobId === job.jobId);
        if (idx >= 0) state.jobs[idx] = job;
        else state.jobs.push(job);
        if (!state.jobIds.includes(job.jobId)) state.jobIds.push(job.jobId);
      }
    },
    /**
     * Marks a job's files as downloaded, as the API records them once GET /translated-files/{id} has served them.
     * A settled batch is no longer polled, so the flag would not come back from the API until the next read. Only
     * the flag changes: the rest of the job stays whatever the slice holds by then.
     */
    markFilesDownloaded(state, action: PayloadAction<{ jobId: string; fileIds: string[] }>) {
      const job = state.jobs.find((j) => j.jobId === action.payload.jobId);
      job?.translatedFiles.forEach((file) => {
        if (action.payload.fileIds.includes(file.id)) file.downloaded = true;
      });
    },
    resetFlow() {
      return initialState;
    },
  },
});

export const {
  setStep,
  setUploadBatch,
  setLanguages,
  setSourceLanguage,
  setTargetLanguages,
  setLocalFiles,
  addLocalFiles,
  removeLocalFile,
  setFiles,
  setRiskAnalysis,
  setOverride,
  clearOverrides,
  addJobId,
  updateJobs,
  upsertJob,
  mergeJobs,
  markFilesDownloaded,
  resetFlow,
} = translationFlowSlice.actions;

export default translationFlowSlice.reducer;
